import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormBuilder, FormGroup, FormArray, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ContactUs } from 'src/app/shared/services/contact-us/contact-us';
import { ContactUsService } from 'src/app/shared/services/contact-us/contact-us.service';
import { ErrorComponent, Helpers, InformationComponent } from 'src/app/shared/util/helpers';
import { catchError, finalize, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-calculator4',
  templateUrl: './calculator4.component.html',
  styleUrls: ['./calculator4.component.scss']
})
export class Calculator4Component implements OnInit {
  public contactItem: ContactUs = new ContactUs();

  public formGroup: FormGroup;

  // Input values
  public monthlyRent: number;
  public annualRepairExpenses: number;
  public taxSystem: string;
  public selectedYear: number;
  
  // Available years
  public availableYears: number[] = [];

  // Calculated values
  public annualIncome: number;
  public deductibleExpenses: number;
  public taxableIncome: number;
  public incomeTax: number;
  public healthInsurance: number;
  public totalTax: number;
  public netIncome: number;

  constructor(
    public snackBar: MatSnackBar,
    private fb: FormBuilder,
    public contactUsService: ContactUsService,
    public translate: TranslateService
  ) {
    this.monthlyRent = 0;
    this.annualRepairExpenses = 0;
    this.taxSystem = 'real';
    this.selectedYear = new Date().getFullYear();
    this.annualIncome = 0;
    this.deductibleExpenses = 0;
    this.taxableIncome = 0;
    this.incomeTax = 0;
    this.healthInsurance = 0;
    this.totalTax = 0;
    this.netIncome = 0;
    
    // Generate available years (current year - 6 to current year)
    this.generateYears();
  }
  
  private generateYears(): void {
    const currentYear = new Date().getFullYear();
    this.availableYears = [];
    for (let i = 6; i >= 0; i--) {
      this.availableYears.push(currentYear - i);
    }
  }

  private getMinimumWage(year: number): number {
    // Salariul minim brut pe țară (RON/lună)
    const minimumWages: { [key: number]: number } = {
      2019: 2080,
      2020: 2230,
      2021: 2300,
      2022: 2550,
      2023: 3300, // Octombrie 2023
      2024: 3700, // Iulie 2024
      2025: 4050
    };
    return minimumWages[year] || 4050; // Default to current year
  }

  private convertEurToRon(euroAmount: number, year: number): number {
    // Cursuri medii EUR/RON aproximative
    const exchangeRates: { [key: number]: number } = {
      2019: 4.75,
      2020: 4.84,
      2021: 4.92,
      2022: 4.93,
      2023: 4.95,
      2024: 4.98,
      2025: 5.00
    };
    return euroAmount * (exchangeRates[year] || 5.00);
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group({
      monthlyRent: [500,],
      annualRepairExpenses: [1000,],
      taxSystem: ['real',],
      selectedYear: [this.selectedYear,],
    });
  }

  calculate() {
    this.monthlyRent = this.formGroup.value['monthlyRent'];
    this.annualRepairExpenses = this.formGroup.value['annualRepairExpenses'];
    this.taxSystem = this.formGroup.value['taxSystem'];
    this.selectedYear = this.formGroup.value['selectedYear'];

    // Calculate annual income (in EUR)
    this.annualIncome = this.monthlyRent * 12;

    // Convert to RON for CASS calculation
    const annualIncomeRon = this.convertEurToRon(this.annualIncome, this.selectedYear);
    const minimumWage = this.getMinimumWage(this.selectedYear);

    // Calculate taxable income based on year and system
    if (this.taxSystem === 'real') {
      // Real system (Sistem real)
      
      // Calculate VI based on year
      if (this.selectedYear >= 2019 && this.selectedYear <= 2022) {
        // 2019-2022: cheltuieli forfetare 40%
        this.taxableIncome = 0.60 * this.annualIncome;
        this.deductibleExpenses = 0.40 * this.annualIncome;
      } else {
        // 2023-2025: cheltuieli forfetare 20%
        this.taxableIncome = 0.80 * this.annualIncome;
        this.deductibleExpenses = 0.20 * this.annualIncome;
      }
      
      // Income tax: 10% on taxable income
      this.incomeTax = this.taxableIncome * 0.10;
      
    } else {
      // Flat rate system (Normă de venit)
      // For norma de venit, use similar percentage as real system
      if (this.selectedYear >= 2019 && this.selectedYear <= 2022) {
        this.taxableIncome = 0.60 * this.annualIncome;
        this.deductibleExpenses = 0.40 * this.annualIncome;
      } else {
        this.taxableIncome = 0.80 * this.annualIncome;
        this.deductibleExpenses = 0.20 * this.annualIncome;
      }
      
      // Income tax: 10%
      this.incomeTax = this.taxableIncome * 0.10;
    }

    // Calculate CASS based on year
    const taxableIncomeRon = this.convertEurToRon(this.taxableIncome, this.selectedYear);
    
    if (this.selectedYear >= 2019 && this.selectedYear <= 2022) {
      // 2019-2022: CASS rules
      if (annualIncomeRon < 12 * minimumWage) {
        this.healthInsurance = 0;
      } else {
        // CASS = 10% × (12 × SMB), convert back to EUR
        const cassRon = 0.10 * (12 * minimumWage);
        this.healthInsurance = cassRon / this.convertEurToRon(1, this.selectedYear);
      }
    } else {
      // 2023-2025: CASS rules with multiple bands
      if (annualIncomeRon < 6 * minimumWage) {
        this.healthInsurance = 0;
      } else if (annualIncomeRon < 12 * minimumWage) {
        // CASS = 10% × (6 × SMB)
        const cassRon = 0.10 * (6 * minimumWage);
        this.healthInsurance = cassRon / this.convertEurToRon(1, this.selectedYear);
      } else if (annualIncomeRon < 24 * minimumWage) {
        // CASS = 10% × (12 × SMB)
        const cassRon = 0.10 * (12 * minimumWage);
        this.healthInsurance = cassRon / this.convertEurToRon(1, this.selectedYear);
      } else {
        // CASS = 10% × (24 × SMB)
        const cassRon = 0.10 * (24 * minimumWage);
        this.healthInsurance = cassRon / this.convertEurToRon(1, this.selectedYear);
      }
    }

    // Total tax
    this.totalTax = this.incomeTax + this.healthInsurance;
    
    // Net income (actual money you keep after taxes and expenses)
    // Subtract both taxes and actual repair expenses from annual income
    this.netIncome = this.annualIncome - this.totalTax - this.annualRepairExpenses;
  }

  getTooltipText(): string {
    return this.translate.instant('CALCULATOR4.TAX_SYSTEM_INFO');
  }

  isLoading = false;
  contactSuccess = false;

  send(): void {
    if (!this.contactItem.subject || !this.contactItem.email || !this.contactItem.content) {
      this.snackBar.openFromComponent(ErrorComponent, {
        data: { message: 'Vă rugăm să completați toate câmpurile.' },
        duration: 2000,
      });
      return;
    }

    this.isLoading = true;
    this.contactSuccess = false;
    this.contactItem.date = new Date();

    this.contactUsService
      .addItem(this.contactItem)
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe(
        () => {
          this.contactItem.subject = '';
          this.contactItem.email = '';
          this.contactItem.content = '';
          this.contactSuccess = true;
          this.snackBar.openFromComponent(InformationComponent, {
            data: { message: 'Mesajul a fost trimis cu succes!' },
            duration: 2000,
          });
        },
        () => {
          this.snackBar.openFromComponent(ErrorComponent, {
            data: { message: 'A apărut o eroare. Vă rugăm să încercați din nou.' },
            duration: 2000,
          });
        }
      );
  }
}

